import { useEffect, useState } from "react"
import Dialog from "@/v2-ui/dialog"
import Placeholder from "@/v2-ui/Placeholder"
import Button from "@/v2-backoffice-client/components/button"
import Tabs from "@/v2-backoffice-client/components/tabs"
import LayerOrganizeCategoriesTab from "@/v2-backoffice-client/layer/organize/LayerOrganizeCategoriesTab"
import LayerOrganizeDrawOrderTab from "@/v2-backoffice-client/layer/organize/LayerOrganizeDrawOrderTab"
import { useLayerOrganizeCatalog } from "@/v2-backoffice-client/layer/organize/layer.organize.catalog"
import { useLayerOrganizeSave } from "@/v2-backoffice-client/layer/organize/layer.organize.save"
import { UNCATEGORIZED_CODE } from "@/v2-backoffice-client/layer/organize/layer.organize.constants"
import { computeOrderUpdates } from "@/v2-backoffice-client/layer/order/layer.order.utils"

export type LayerOrganizeDialogProps = {
  open: boolean
  onOpenChange(open: boolean): void
}

/**
 * The catalog organizer: one dialog for everything order-related.
 *
 * - "Categories" mirrors the app's Add-layers chooser: drag categories to
 *   reorder the sections, select one and drag its layers to reorder them
 *   within the section. A category is just a filtered view over the one
 *   global layer ranking, so an in-section move also moves the layer in
 *   the draw order (and in any other category it belongs to).
 * - "Draw order" is the flat global list for map-stacking fine-tuning:
 *   top of the list = drawn on top of the map.
 *
 * This shell owns the tab layout, the pending orders (lifted here because
 * the global ranking is shared by both tabs, the selection must survive a
 * tab switch, and the one Save button commits everything), and the save.
 * Saving computes the minimal set of Priority rewrites for both dimensions
 * and writes them in two batch mutations, dual-writing the authoring
 * catalog so the curated order survives the next publish.
 */
function LayerOrganizeDialog(props: LayerOrganizeDialogProps) {
  const { open, onOpenChange } = props

  const { layers, categories, layersByCode, categoriesByCode, isLoading } =
    useLayerOrganizeCatalog({ skip: !open })
  const { save, isSaving } = useLayerOrganizeSave()

  // Both orders ascending Priority: globalOrder = the chooser's top-to-
  // bottom order (the draw-order tab renders it reversed — highest rank is
  // drawn on top of the map).
  const [globalOrder, setGlobalOrder] = useState<string[] | null>(null)
  const [categoryOrder, setCategoryOrder] = useState<string[] | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setGlobalOrder(null)
      setCategoryOrder(null)
      setSelectedCategory(null)
      return
    }
    if (!globalOrder && layers.length) {
      setGlobalOrder(
        [...layers]
          .sort((a, b) => a.Priority - b.Priority)
          .map((layer) => layer.FeatureCode),
      )
    }
    if (!categoryOrder && categories.length) {
      const ordered = [...categories]
        .filter((category) => category.FeatureCode !== UNCATEGORIZED_CODE)
        .sort((a, b) => a.Priority - b.Priority)
        .map((category) => category.FeatureCode)
      setCategoryOrder(ordered)
      setSelectedCategory((current) => current ?? ordered[0] ?? null)
    }
  }, [open, globalOrder, categoryOrder, layers, categories])

  const layerUpdates = globalOrder
    ? computeOrderUpdates(
        globalOrder
          .map((code) => layersByCode.get(code))
          .filter((layer) => layer !== undefined)
          .map((layer) => ({
            FeatureCode: layer.FeatureCode,
            Priority: layer.Priority,
          })),
      )
    : []

  const categoryUpdates = categoryOrder
    ? computeOrderUpdates(
        categoryOrder
          .map((code) => categoriesByCode.get(code))
          .filter((category) => category !== undefined)
          .map((category) => ({
            FeatureCode: category.FeatureCode,
            Priority: category.Priority,
          })),
      )
    : []

  const changeCount = layerUpdates.length + categoryUpdates.length

  async function onSave() {
    if (changeCount === 0) {
      onOpenChange(false)
      return
    }
    const didSave = await save({ categoryUpdates, layerUpdates })
    if (didSave) onOpenChange(false)
  }

  const isLoaded = globalOrder !== null && categoryOrder !== null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-4xl">
        <Dialog.Header>
          <Dialog.Title>Organize layers</Dialog.Title>
          <Dialog.Description>
            Curate what users see in the app&apos;s layer chooser: the order of
            the categories and of the layers inside each one.
          </Dialog.Description>
        </Dialog.Header>

        {!isLoaded && (
          <Placeholder className="h-64">
            {isLoading ? "Loading the catalog…" : "Nothing to organize."}
          </Placeholder>
        )}

        {isLoaded && (
          <Tabs
            id="layer-organize"
            defaultValue="categories"
            className="flex flex-col"
          >
            <Tabs.List className="w-fit">
              <Tabs.Trigger value="categories">Categories</Tabs.Trigger>
              <Tabs.Trigger value="draw">Draw order</Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="categories" className="pt-2">
              <LayerOrganizeCategoriesTab
                globalOrder={globalOrder}
                categoryOrder={categoryOrder}
                selectedCategory={selectedCategory}
                layersByCode={layersByCode}
                categoriesByCode={categoriesByCode}
                onGlobalOrderChange={setGlobalOrder}
                onCategoryOrderChange={setCategoryOrder}
                onSelectedCategoryChange={setSelectedCategory}
              />
            </Tabs.Content>

            <Tabs.Content value="draw" className="pt-2">
              <LayerOrganizeDrawOrderTab
                globalOrder={globalOrder}
                layersByCode={layersByCode}
                onGlobalOrderChange={setGlobalOrder}
              />
            </Tabs.Content>
          </Tabs>
        )}

        <Dialog.Footer>
          <Button
            variant="ghost"
            data-feature-code="UI_BackofficeLayers_Organize_Cancel"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            isLoading={isSaving}
            disabled={changeCount === 0}
            data-feature-code="UI_BackofficeLayers_Organize_Save"
            onClick={() => void onSave()}
          >
            {changeCount > 0
              ? `Save order (${changeCount} change${changeCount === 1 ? "" : "s"})`
              : "No changes"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LayerOrganizeDialog
